import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { tKey } from 'ember-intl';
import { ROOMTYPES } from 'frontend-utils/models/roomtype';
import {
  OTA_IDS,
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import {
  COLOR_CHART_SELECTED,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';

export const FIRST_HEADER_HEIGHT = 60; // This is the height of the top navigation bar on the application

export const SECOND_HEADER_HEIGHT = 60; // This is the height of the second header on the application

export const HEADER_HEIGHT = FIRST_HEADER_HEIGHT + SECOND_HEADER_HEIGHT; // This is the total height of the top navigation bar on the application

// The height of the third subheader (3rd header with the filter bar)
export const SUB_HEADER_HEIGHT = 48;

export const APIGATEWAY = 'apigateway/v1/app';

export const MAX_LOS_BOOKING = 45;
export const MAX_LOS_EXPEDIA = 28;

export const RATE_NO_VALUE = 0;

export const STANDARD_ROOMS = [
  'budget',
  'standard',
  'single',
  'premium',
  'suite',
  'family',
  'apartment',
  'bungalow',
  'parkandfly',
  'sharedbathroom',
  'dormitory',
  'camper',
  'villa',
];

export const OTA_IDS_WITH_4_GUESTS = [
  OTA_IDS.RAKUTEN,
  OTA_IDS.IKYU,
  OTA_IDS.JALAN,
];

export const DAY_OF_WEEK_TRANSLATIONS = {
  1: 'days.mon',
  2: 'days.tue',
  3: 'days.wed',
  4: 'days.thu',
  5: 'days.fri',
  6: 'days.sat',
  7: 'days.sun',
};

export const DAY_OF_WEEK_SHORT_TRANSLATIONS = {
  1: 'days_short.mon',
  2: 'days_short.tue',
  3: 'days_short.wed',
  4: 'days_short.thu',
  5: 'days_short.fri',
  6: 'days_short.sat',
  7: 'days_short.sun',
};

export const DAY_IN_SECONDS = 24 * 60 * 60;

// Ratetype constants
export const RATETYPES = {
  NONE: 0, // Used in compare and liveupdate
  THIRD_PARTY: 1,
} as const;

export type RateType = (typeof RATETYPES)[keyof typeof RATETYPES];

// Mapping of bar, flexible and rate_type params
// bar and flexible are the soft filters for the best_flex and cancellable QP
// See https://otainsight.slack.com/archives/C015VTDMZ4G/p1640357248024700?thread_ts=1640357069.021000&cid=C015VTDMZ4G
export const RATE_PARAMS = {
  BEST_FLEX: 'bestflex',
  LOWEST: 'lowest',
  FLEXIBLE: 'flex', // Named Lowest Flex in app
  THIRD_PARTY: 'thirdparty',
} as const;

export type RateParam = (typeof RATE_PARAMS)[keyof typeof RATE_PARAMS];

export const BEST_FLEX = true;
export const FLEXIBLE = true;

// Mealtype constants
export const MEALTYPES = {
  ANY: 0,
  BED_AND_BREAKFAST: 1,
  HALF_BOARD: 2,
  FULL_BOARD: 3,
  ALL_IN: 4,
  ROOM_ONLY: 5,
  WALK_IN_BREAKFAST: 6,
  INCLUSIVE_BREAKFAST: 7,
} as const;

export type MealTypeValues = (typeof MEALTYPES)[keyof typeof MEALTYPES];

export const ORDERED_MEALTYPES: readonly MealTypeValues[] = [
  MEALTYPES.ANY,
  MEALTYPES.ROOM_ONLY,
  MEALTYPES.BED_AND_BREAKFAST,
  MEALTYPES.WALK_IN_BREAKFAST,
  MEALTYPES.INCLUSIVE_BREAKFAST,
  MEALTYPES.HALF_BOARD,
  MEALTYPES.FULL_BOARD,
  MEALTYPES.ALL_IN,
] as const;

export const MEALTYPE_TRANSLATION_KEYS = {
  [MEALTYPES.ANY]: 'rates.meal.any',
  [MEALTYPES.BED_AND_BREAKFAST]: tKey('rates.meal.breakfast'),
  [MEALTYPES.HALF_BOARD]: tKey('rates.meal.half_board'),
  [MEALTYPES.FULL_BOARD]: tKey('rates.meal.full_board'),
  [MEALTYPES.ALL_IN]: tKey('rates.meal.all_inclusive'),
  [MEALTYPES.ROOM_ONLY]: 'rates.meal.room_only',
  [MEALTYPES.WALK_IN_BREAKFAST]: tKey('rates.meal.walk_in_breakfast'),
  [MEALTYPES.INCLUSIVE_BREAKFAST]: tKey('rates.meal.inclusive_breakfast'),
} as const;

export const MEALTYPE_ICONS: Record<MealTypeValues, IconName | undefined> = {
  [MEALTYPES.ANY]: undefined,
  [MEALTYPES.BED_AND_BREAKFAST]: 'meals-bed-and-breakfast',
  [MEALTYPES.HALF_BOARD]: 'meals-half-meal',
  [MEALTYPES.FULL_BOARD]: 'meals-full-meal',
  [MEALTYPES.ALL_IN]: 'meals-all-inclusive',
  [MEALTYPES.ROOM_ONLY]: 'meals-room-only',
  [MEALTYPES.WALK_IN_BREAKFAST]: 'meals-bed-and-breakfast',
  [MEALTYPES.INCLUSIVE_BREAKFAST]: 'meals-bed-and-breakfast',
};

// Membershiptype constants
export const MEMBERSHIPTYPES = {
  PUBLIC: 0,
  MEMBER_ONLY: 1,
  AAA: 2,
  SENIOR: 3,
  GOVERNMENT: 4,
  AARP: 5,
  MILITARY: 6,
  CORPORATE: 7,
  RESIDENT: 8,
  COUPON: 9,
} as const;

export type MembershipType =
  (typeof MEMBERSHIPTYPES)[keyof typeof MEMBERSHIPTYPES];

export const ORDERED_MEMBER_RATES = [
  'membership_branddotcom_weekday',
  'membership_branddotcom_weekend',
  'membership_bookingdotcom',
  'membership_expedia',
];

export const RATE_PARAMS_TO_TRANSLATION_KEYS = {
  [RATE_PARAMS.BEST_FLEX]: 'rates.bar_short',
  [RATE_PARAMS.LOWEST]: 'rates.lowest',
  [RATE_PARAMS.FLEXIBLE]: 'rates.flexible',
  [RATE_PARAMS.THIRD_PARTY]: 'rates.third_party',
} as const;

export const MEMBERSHIPTYPE_TRANSLATION_KEYS = {
  [MEMBERSHIPTYPES.PUBLIC]: tKey('membershiptypes.public'),
  [MEMBERSHIPTYPES.MEMBER_ONLY]: tKey('membershiptypes.member_only'),
  [MEMBERSHIPTYPES.AAA]: tKey('membershiptypes.aaa'),
  [MEMBERSHIPTYPES.SENIOR]: tKey('membershiptypes.senior'),
  [MEMBERSHIPTYPES.GOVERNMENT]: tKey('membershiptypes.government'),
  [MEMBERSHIPTYPES.AARP]: tKey('membershiptypes.aarp'),
  [MEMBERSHIPTYPES.MILITARY]: tKey('membershiptypes.military'),
  [MEMBERSHIPTYPES.CORPORATE]: tKey('membershiptypes.corporate'),
  [MEMBERSHIPTYPES.RESIDENT]: tKey('membershiptypes.resident'),
  [MEMBERSHIPTYPES.COUPON]: tKey('parity_strategy.membership.coupon'),
};

// Platform constants
export const PLATFORM_TRANSLATION_KEYS: Record<Platform, string> = {
  [PLATFORMS.PLATFORM_UNSPECIFIED]: tKey('rates.platform.0'),
  [PLATFORMS.PLATFORM_DESKTOP]: tKey('rates.platform.0'),
  [PLATFORMS.PLATFORM_MOBILE_WEBSITE]: tKey('rates.platform.1'),
  [PLATFORMS.PLATFORM_MOBILE_APP]: tKey('rates.platform.2'),
} as const;

export const PLATFORM_ICONS: Record<Platform, IconName> = {
  [PLATFORMS.PLATFORM_UNSPECIFIED]: 'devices-desktop',
  [PLATFORMS.PLATFORM_DESKTOP]: 'devices-desktop',
  [PLATFORMS.PLATFORM_MOBILE_WEBSITE]: 'devices-phone',
  [PLATFORMS.PLATFORM_MOBILE_APP]: 'devices-app',
};

export const POSITION_TO_BASERATE = {
  NO_DATA: -3,
  LOSS_SOLDOUT: -2, // Brand rate is soldout
  LOSS_PRICE_DIFFERENCE: -1,
  MEET: 0,
  WIN_PRICE_DIFFERENCE: 1,
  WIN_SOLDOUT: 2, // Channel rate is soldout
} as const;

export type PositionToBaserate =
  (typeof POSITION_TO_BASERATE)[keyof typeof POSITION_TO_BASERATE];

// SWLY constants
export const SWLY_TRANSLATION_KEYS = {
  true: 'revenue_insight.swly_short',
  SELECTED_DATE: 'rates.compare.selected_date',
};

// Date constants
export const DATE_TRANSLATION_KEYS = {
  DAYS_AGO: 'rates.compare.days_ago',
};

export const OTA_GROUPS = {
  ALL: 'all',
  MAJOR: 'major',
} as const;

export const CUSTOM_ROOMTYPE_PREFIX = 'c_';

export const ROOMTYPE_TRANSLATION_KEYS = {
  [ROOMTYPES.ALL]: 'rates.roomtypes.all',
  [ROOMTYPES.BUDGET]: tKey('rates.roomtypes.budget'),
  [ROOMTYPES.STANDARD]: tKey('rates.roomtypes.standard'),
  [ROOMTYPES.SINGLE]: tKey('rates.roomtypes.single'),
  [ROOMTYPES.PREMIUM]: tKey('rates.roomtypes.premium'),
  [ROOMTYPES.SUITE]: tKey('rates.roomtypes.suite'),
  [ROOMTYPES.FAMILY]: tKey('rates.roomtypes.family'),
  [ROOMTYPES.APARTMENT]: tKey('rates.roomtypes.apartment'),
  [ROOMTYPES.BUNGALOW]: tKey('rates.roomtypes.bungalow'),
  [ROOMTYPES.PARK_AND_FLY]: tKey('rates.roomtypes.parkandfly'),
  [ROOMTYPES.SHARED_BATHROOM]: tKey('rates.roomtypes.sharedbathroom'),
  [ROOMTYPES.DORMITORY]: tKey('rates.roomtypes.dormitory'),
  [ROOMTYPES.CAMPER]: tKey('rates.roomtypes.camper'),
  [ROOMTYPES.VILLA]: tKey('rates.roomtypes.villa'),
};

// Compare constants
export const COMPARETYPES = {
  RATE_PARAM: 'rate_param',
  MEALTYPE: 'mealtype',
  MEMBERSHIPTYPE: 'membershiptype',
  LOS: 'los',
  PERSONS: 'persons',
  PLATFORM: 'platform',
  SWLY: 'swly',
  DATE: 'extract_date',
  NO_COMPARE: '',
  ROOMTYPE: 'roomtype',
} as const;

export type CompareType = (typeof COMPARETYPES)[keyof typeof COMPARETYPES];

export const COMPARE_DIFFERENCE_VISUALISATION_MAPPING = {
  [COMPARETYPES.RATE_PARAM]: 'percentage',
  [COMPARETYPES.MEALTYPE]: 'value',
  [COMPARETYPES.MEMBERSHIPTYPE]: 'percentage',
  [COMPARETYPES.LOS]: 'percentage',
  [COMPARETYPES.PERSONS]: 'percentage',
  [COMPARETYPES.PLATFORM]: 'percentage',
  [COMPARETYPES.SWLY]: 'value',
  [COMPARETYPES.DATE]: 'value',
  [COMPARETYPES.ROOMTYPE]: 'value',
  '': 'value',
} as const;

export const COMPARE_MESSAGES = {
  BAR: tKey('compare.bar_change'),
  CANCELLABLE: tKey('compare.cancellable_change'),
  RATETYPE: tKey('compare.ratetype_change'),
  MEMBERSHIPTYPE: tKey('compare.membershiptype_change'),
  ROOMTYPE: tKey('compare.roomtype_change'),
};

export const COMPARE_MESSAGE_TO_ICON_NAME = {
  [COMPARE_MESSAGES.BAR]: 'rates-best-flex',
  [COMPARE_MESSAGES.CANCELLABLE]: 'rates-semi-flex',
  [COMPARE_MESSAGES.RATETYPE]: 'rates-third-party',
  [COMPARE_MESSAGES.MEMBERSHIPTYPE]: 'user',
  [COMPARE_MESSAGES.ROOMTYPE]: 'room-type-change',
};

export const COMPARE_TYPE_TO_MESSAGE = {
  [COMPARETYPES.MEMBERSHIPTYPE]: COMPARE_MESSAGES.MEMBERSHIPTYPE,
  [COMPARETYPES.ROOMTYPE]: COMPARE_MESSAGES.ROOMTYPE,
};

export const COMPARETYPES_TO_QUERYPARAMS = {
  [COMPARETYPES.RATE_PARAM]: 'rateParam',
  [COMPARETYPES.MEALTYPE]: 'mealPlan',
  [COMPARETYPES.MEMBERSHIPTYPE]: 'membershipType',
  [COMPARETYPES.LOS]: 'los',
  [COMPARETYPES.PERSONS]: 'maxPersons',
  [COMPARETYPES.PLATFORM]: 'platform',
  [COMPARETYPES.SWLY]: 'month',
  [COMPARETYPES.DATE]: 'month',
  [COMPARETYPES.ROOMTYPE]: 'roomType',
};

export const COMPARETYPES_TO_TRANSLATIONMAPPINGS = {
  [COMPARETYPES.RATE_PARAM]: RATE_PARAMS_TO_TRANSLATION_KEYS,
  [COMPARETYPES.MEALTYPE]: MEALTYPE_TRANSLATION_KEYS,
  [COMPARETYPES.MEMBERSHIPTYPE]: MEMBERSHIPTYPE_TRANSLATION_KEYS,
  [COMPARETYPES.LOS]: 'rates.nights',
  [COMPARETYPES.PERSONS]: 'rates.persons_selection',
  [COMPARETYPES.PLATFORM]: PLATFORM_TRANSLATION_KEYS,
  [COMPARETYPES.SWLY]: SWLY_TRANSLATION_KEYS,
  [COMPARETYPES.DATE]: DATE_TRANSLATION_KEYS,
  [COMPARETYPES.ROOMTYPE]: ROOMTYPE_TRANSLATION_KEYS,
};

export const RATE_INSIGHT_FILTER_TYPE_RATE_PARAM = 'rate_param';
export const RATE_INSIGHT_FILTER_TYPE_MEMBERSHIPTYPE = 'membershiptype';
export const RATE_INSIGHT_FILTER_TYPE_MEALTYPE = 'mealtype';
export const RATE_INSIGHT_FILTER_TYPE_LOS = 'los';
export const RATE_INSIGHT_FILTER_TYPE_PERSONS = 'persons';
export const RATE_INSIGHT_FILTER_TYPE_PLATFORM = 'platform';

export const COMPONENT_DOMAINS = {
  RATES: 'rates',
  PARITY: 'parity',
  PARITY_META: 'parity.meta',
};

export const ACCESS_RIGHTS = [
  { key: 'admin', value: tKey('useraccess_rights_admin') },
  { key: 'write', value: tKey('useraccess_rights_write') },
  { key: 'read', value: tKey('useraccess_rights_read') },
];

export const REPORT_IDS = {
  EXCEL: 'excel_export',
  SCHEDULED: 'scheduled_report',
};

export const SORT_ORDER = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
};
export type SortOrderType = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];

export const THIRD_PARTY_LABEL = 'thirdparty';

export const AVAILABLE_RATECHANGES = [1, 3, 7, 14];

export const BI_UPSELL_CONTACT_LOCALSTORAGE_KEY = 'app-bi-upsell-request-sent';

export const MI_UPSELL_CONTACT_LOCALSTORAGE_KEY =
  'app-market-insight-upsell-request-sent';

export const MI_SEARCHES_TYPE_FLIGHTS_LEISURE = 0;
export const MI_SEARCHES_TYPE_HOTELS_BUSINESS = 1;
export const MI_SEARCHES_TYPE_HOTELS_LEISURE = 2;
export const MI_SEARCHES_TYPE_FLIGHTS_BUSINESS = 3;

export const RATES_OUT_OF_SYNC_THRESHOLD = 0.5; // Threshold for when we consider a collections of rates out of sync (now 50%)

export const HALF_DAY_IN_MS = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
export const DAY_IN_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
export const THREE_DAYS_IN_MS = 72 * 60 * 60 * 1000; // 3 days in milliseconds
export const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
export const MONTH_IN_MS = 28 * 24 * 60 * 60 * 1000; // 28 days (shortest month) in milliseconds

export const HALF_DAY = 'half_day';
export const DAY = 'day';
export const WEEK = 'week';
export const MONTH = 'month';

export const DATE_AGGREGATION = {
  DAY,
  WEEK,
  MONTH,
} as const;
export type DateAggregationType =
  (typeof DATE_AGGREGATION)[keyof typeof DATE_AGGREGATION];

export const CHART_INTERVALS = {
  [HALF_DAY]: HALF_DAY_IN_MS,
  [DAY]: DAY_IN_MS,
  [WEEK]: WEEK_IN_MS,
  [MONTH]: MONTH_IN_MS,
};

export const RI_DEFAULT_DATE_FORMAT = 'll';

export const MI_DEFAULT_DATE_FORMAT = 'll';

// Note: since the dayjs locale is used, we used the dayjs specific locale strings (so en-gb instead of en for example)
// See https://momentjs.com/docs/#/displaying/
export const PARTIAL_DATE_FORMATS = {
  ja: {
    YEAR_MONTH_AND_DAY_SHORT: 'YYYY/MM/DD',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\d\d\/\d\d\/\d\d/,
    YEAR_AND_MONTH_SHORT: 'YYYY/MM',
    YEAR_AND_MONTH_LONG: 'YYYY年M月',
    MONTH_AND_DAY_SHORT: 'MM/DD',
    MONTH_AND_DAY_LONG: 'M月D日',
    WEEKDAY_AND_DATE_LONG: 'LL dddd',
    WEEKDAY_AND_DATE_MEDIUM: 'LL ddd',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'MM/DD ddd',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'MMM DD ddd',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'YYYY/MM/DD ddd',
  },
  en: {
    YEAR_MONTH_AND_DAY_SHORT: 'MM/DD/YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\/\d\d\/\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM/YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM YYYY',
    MONTH_AND_DAY_SHORT: 'MM/DD',
    MONTH_AND_DAY_LONG: 'MMMM D',
    WEEKDAY_AND_DATE_LONG: 'dddd LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd MM/DD',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd MMM DD',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd MM/DD/YYYY',
  },
  'en-gb': {
    YEAR_MONTH_AND_DAY_SHORT: 'DD/MM/YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\/\d\d\/\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM/YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM YYYY',
    MONTH_AND_DAY_SHORT: 'DD/MM',
    MONTH_AND_DAY_LONG: 'D MMMM',
    WEEKDAY_AND_DATE_LONG: 'dddd LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd DD/MM',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd DD MMM',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd DD/MM/YYYY',
  },
  de: {
    YEAR_MONTH_AND_DAY_SHORT: 'DD.MM.YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\.\d\d\.\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM.YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM YYYY',
    MONTH_AND_DAY_SHORT: 'DD.MM',
    MONTH_AND_DAY_LONG: 'D. MMMM',
    WEEKDAY_AND_DATE_LONG: 'dddd LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd DD.MM',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd DD MMM',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd DD.MM.YYYY',
  },
  fr: {
    YEAR_MONTH_AND_DAY_SHORT: 'DD/MM/YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\/\d\d\/\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM/YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM YYYY',
    MONTH_AND_DAY_SHORT: 'DD/MM',
    MONTH_AND_DAY_LONG: 'D MMMM',
    WEEKDAY_AND_DATE_LONG: 'dddd LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd DD/MM',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd DD MMM',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd DD/MM/YYYY',
  },
  es: {
    YEAR_MONTH_AND_DAY_SHORT: 'DD/MM/YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\/\d\d\/\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM/YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM [de] YYYY',
    MONTH_AND_DAY_SHORT: 'DD/MM',
    MONTH_AND_DAY_LONG: 'D [de] MMMM',
    WEEKDAY_AND_DATE_LONG: 'dddd LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd DD/MM',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd DD MMM',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd DD/MM/YYYY',
  },
  'pt-br': {
    YEAR_MONTH_AND_DAY_SHORT: 'DD/MM/YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\/\d\d\/\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM/YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM [de] YYYY',
    MONTH_AND_DAY_SHORT: 'DD/MM',
    MONTH_AND_DAY_LONG: 'D [de] MMMM',
    WEEKDAY_AND_DATE_LONG: 'dddd, LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd, LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd, ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd, DD/MM',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd, DD [de] MMM',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd, DD/MM/YYYY',
  },
  it: {
    YEAR_MONTH_AND_DAY_SHORT: 'DD/MM/YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\/\d\d\/\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM/YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM YYYY',
    MONTH_AND_DAY_SHORT: 'DD/MM',
    MONTH_AND_DAY_LONG: 'D MMMM',
    WEEKDAY_AND_DATE_LONG: 'dddd LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd DD/MM',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd DD MMM',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd DD/MM/YYYY',
  },
  nl: {
    YEAR_MONTH_AND_DAY_SHORT: 'DD/MM/YYYY',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\/\d\d\/\d\d\d\d/,
    YEAR_AND_MONTH_SHORT: 'MM/YYYY',
    YEAR_AND_MONTH_LONG: 'MMMM YYYY',
    MONTH_AND_DAY_SHORT: 'DD/MM',
    MONTH_AND_DAY_LONG: 'D MMMM',
    WEEKDAY_AND_DATE_LONG: 'dddd LL',
    WEEKDAY_AND_DATE_MEDIUM: 'ddd LL',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'ddd DD/MM',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'ddd DD MMM',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'ddd DD/MM/YYYY',
  },
  'zh-tw': {
    YEAR_MONTH_AND_DAY_SHORT: 'YYYY/MM/DD',
    YEAR_MONTH_AND_DAY_REGEX: /\d\d\d\d\/\d\d\/\d\d/,
    YEAR_AND_MONTH_SHORT: 'YYYY/MM',
    YEAR_AND_MONTH_LONG: 'YYYY年M月',
    MONTH_AND_DAY_SHORT: 'MM/DD',
    MONTH_AND_DAY_LONG: 'M月D日',
    WEEKDAY_AND_DATE_LONG: 'LL dddd',
    WEEKDAY_AND_DATE_MEDIUM: 'll dddd',
    WEEKDAY_AND_DATE_SHORT: 'ddd ll',
    WEEKDAY_AND_MONTH_AND_DAY_SHORT: 'MM/DD ddd',
    WEEKDAY_AND_MONTH_AND_DAY_MEDIUM: 'MMM DD ddd',
    WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT: 'YYYY/MM/DD ddd',
  },
} as const;
export type PartialDateFormatType =
  (typeof PARTIAL_DATE_FORMATS)[keyof typeof PARTIAL_DATE_FORMATS];

// Marker for missing data (grey cross)
export const HIGHCHARTS_MISSING_MARKER = {
  enabled: true,
  symbol: 'cross',
  lineColor: 'grey',
  lineWidth: 2,
};

export const HIGHCHARTS_SELECTED_MARKER = {
  enabled: true,
  symbol: null,
  fillColor: getCssVariableValue(COLOR_CHART_SELECTED),
  radius: 8,
};

// See https://api.highcharts.com/class-reference/Highcharts#dateFormat
export const HIGHCHARTS_DATETIME_FORMATS = {
  ja: {
    day: '%Y年%m月%d日',
    week: '%m/%d (W%W)',
    month: '%Y年%B',
    year: '%Y',
  },
  en: {
    day: '%e %b %Y',
    week: '%m/%d (W%W)',
    month: '%b %Y',
    year: '%Y',
  },
  'en-gb': {
    day: '%b %e, %Y',
    week: '%d/%m (W%W)',
    month: '%b %Y',
    year: '%Y',
  },
  de: {
    day: '%e. %b. %Y',
    week: '%d.%m (W%W)',
    month: '%b %Y',
    year: '%Y',
  },
  fr: {
    day: '%e %b. %Y',
    week: '%d/%m (W%W)',
    month: '%b %Y',
    year: '%Y',
  },
  es: {
    day: '%e de %b de %Y',
    week: '%d/%m (W%W)',
    month: '%b de %Y',
    year: '%Y',
  },
  'pt-br': {
    day: '%e de %b de %Y',
    week: '%d/%m (W%W)',
    month: '%b de %Y',
    year: '%Y',
  },
  it: {
    day: '%e %b %Y',
    week: '%d/%m (W%W)',
    month: '%b %Y',
    year: '%Y',
  },
  nl: {
    day: '%e %b %Y',
    week: '%d/%m (W%W)',
    month: '%b %Y',
    year: '%Y',
  },
  'zh-tw': {
    day: '%Y年%m月%d日',
    week: '%m/%d (W%W)',
    month: '%Y年%B',
    year: '%Y',
  },
};
export type HighchartsDateTimeFormatType =
  (typeof HIGHCHARTS_DATETIME_FORMATS)[keyof typeof HIGHCHARTS_DATETIME_FORMATS];

export const DRAWER_IDS = {
  BUDGET_FORECAST_VERSION_HISTORY: 'budget-forecast-version-history',
  OD_DASHBOARD_FEED: 'od-dashboard-feed',
  PM_ISSUE_DETAIL_DRAWER: 'pm-issue-detail-drawer',
  SMART_INSIGHTS_DRAWER: 'smart-insights-drawer',
} as const;

export const FILTER_BAR_PAGETYPES = {
  OVERVIEW_DASHBOARD: 'OD',
  MARKET_INSIGHT: 'MI',
  REVENUE_INSIGHT: 'RI', // Note currently both used for RI & RIMP. Could later be split up in RI & RIMP if needed
  PARITY_ANALYTICS: 'PA',
  SETTINGS_PAGE: 'SE', // e.g. used for MI rate preference
  RATE_INSIGHT: 'RATE',
  PARITY_STRATEGY: 'PS',
};

export type FilterBarPageType =
  (typeof FILTER_BAR_PAGETYPES)[keyof typeof FILTER_BAR_PAGETYPES];

export const FILTER_DELIMITER = '.';

export const MARKET_DEMAND_LEVELS = {
  VERY_LOW: 'very-low',
  LOW: 'low',
  NORMAL: 'normal',
  ELEVATED: 'elevated',
  HIGH: 'high',
  VERY_HIGH: 'very-high',
} as const;

export type MarketDemandLevel =
  (typeof MARKET_DEMAND_LEVELS)[keyof typeof MARKET_DEMAND_LEVELS];

export const MARKET_DEMAND_LEVELS_CLASSES = {
  [MARKET_DEMAND_LEVELS.VERY_LOW]: 'bg-chart-demand-background-very-low',
  [MARKET_DEMAND_LEVELS.LOW]: 'bg-chart-demand-background-low',
  [MARKET_DEMAND_LEVELS.NORMAL]: 'bg-chart-demand-background-normal',
  [MARKET_DEMAND_LEVELS.ELEVATED]: 'bg-chart-demand-background-elevated',
  [MARKET_DEMAND_LEVELS.HIGH]: 'bg-chart-demand-background-high',
  [MARKET_DEMAND_LEVELS.VERY_HIGH]: 'bg-chart-demand-background-very-high',
} as const;

export const MARKET_DEMAND_LEVEL_NUMBERS = {
  [MARKET_DEMAND_LEVELS.VERY_LOW]: 1,
  [MARKET_DEMAND_LEVELS.LOW]: 2,
  [MARKET_DEMAND_LEVELS.NORMAL]: 3,
  [MARKET_DEMAND_LEVELS.ELEVATED]: 4,
  [MARKET_DEMAND_LEVELS.HIGH]: 5,
  [MARKET_DEMAND_LEVELS.VERY_HIGH]: 6,
} as const;

export const MARKET_DEMAND_UPPER_BOUNDS = {
  [MARKET_DEMAND_LEVELS.VERY_LOW]: 0.25,
  [MARKET_DEMAND_LEVELS.LOW]: 0.4,
  [MARKET_DEMAND_LEVELS.NORMAL]: 0.6,
  [MARKET_DEMAND_LEVELS.ELEVATED]: 0.7,
  [MARKET_DEMAND_LEVELS.HIGH]: 0.9,
  [MARKET_DEMAND_LEVELS.VERY_HIGH]: 1,
} as const;

export const PRODUCT_NAME_TRANSLATION_KEYS = {
  rate_insight: tKey('general.rate_insight'),
  revenue_insight: tKey('general.revenue_insight'),
  spider_analytics: tKey('general.spider'),
  business_intelligence: tKey('general.bi'),
  parity_insight: tKey('general.parity_insight'),
  market_insight: tKey('general.market_insight'),
  pricing_assistant: tKey('general.pricing_assistant'),
  benchmark_insight: tKey('general.benchmark_insight'),
  rates: tKey('general.rate_insight'),
  par_strategy: tKey('general.parity_insight'),
  par_manager: tKey('general.parity_manager'),
  spider: tKey('general.spider'),
  bi: tKey('general.bi'),
} as const;

export type SupportedProductTranslation =
  keyof typeof PRODUCT_NAME_TRANSLATION_KEYS;

export const SUBSCRIPTION_TYPE_TRANSLATION_KEYS = {
  hotel: tKey('general.hotel'),
  business: tKey('general.subscription_type.business'),
  chain: tKey('general.subscription_type.chain'),
  brand: tKey('general.subscription_type.brand'),
} as const;

export type SupportedSubscriptionTypeTranslation =
  keyof typeof SUBSCRIPTION_TYPE_TRANSLATION_KEYS;

export const SUBSCRIPTION_PRODUCT_ORDER = [
  'market_insight',
  'rate_insight',
  'pricing_assistant',
  'bi',
  'spider',
  'revenue_insight',
  'benchmark_insight',
  'parity_insight',
];

export const MARKET_PRODUCT_ORDER = [
  'rates',
  'par_strategy',
  'par_manager',
  'bi',
];

export const DAY_TABLE_SIZES = {
  NAME_MAX_WIDTH: 300,
  VALUE_WIDTH: 145, // Fixed cell width copied over from Figma
  VALUE_MIN_WIDTH: 145,
} as const;
